/**
 * opinion_lean.ts — the client's READ of a versona's political lean (2026-09-20).
 *
 * A dialogue path has the versona state a position on the levy, and the server
 * nudges that position when the player argues the other side. Both must agree
 * on WHICH side the versona is on — so this mirrors the two pure functions of
 * server/src/lib/opinion_ops.ts that decide it: the MATERIAL ANCHOR (what their
 * circumstances argue for) and the decay of a persuaded lean back toward it.
 * With no opinion row the lean IS the anchor, exactly as `leanOf` reads it.
 *
 * MIRROR LAW: the two function BODIES below are compared against the server's
 * by `verify:dialogue-threads`. Change one, change both.
 */

export interface LeanAxes { boldness: number; greed: number; diligence: number; sociability: number }

export const LEAN_HALF_LIFE_S = 5 * 86_400;
export const LEAN_MAX_X10 = 1000;

/** The lean this agent's CIRCUMSTANCES argue for (pure). */
export function materialAnchorX10(a: LeanAxes, wallet: bigint, hunger: number): number {
  const w = Number(wallet);
  let lean = 0;
  lean += w < 15 ? 350 : w < 60 ? 100 : w > 300 ? -250 : 0;
  lean += hunger > 60 ? 250 : 0;
  lean += (a.sociability - 50) * 3;
  lean -= (a.greed - 50) * 5;
  return Math.max(-LEAN_MAX_X10, Math.min(LEAN_MAX_X10, Math.round(lean)));
}

/** Lean after `elapsedS` untouched — persuasion drains back to the anchor (pure). */
export function decayedLeanX10(storedX10: number, anchorX10: number, elapsedS: number): number {
  if (elapsedS <= 0) return storedX10;
  const f = Math.pow(0.5, elapsedS / LEAN_HALF_LIFE_S);
  return Math.round(anchorX10 + (storedX10 - anchorX10) * f);
}

/** The effective lean NOW — the client twin of the server's `leanOf` (row = null ⇒ the pure anchor). */
export function effectiveLeanX10(
  a: LeanAxes, wallet: bigint, hunger: number,
  row: { leanX10: number; updatedAtMicros: bigint } | null, nowMicros: bigint,
): number {
  const anchor = materialAnchorX10(a, wallet, hunger);
  if (!row) return anchor;
  return decayedLeanX10(row.leanX10, anchor, Number((nowMicros - row.updatedAtMicros) / 1_000_000n));
}
